/**
 * One section of the Auto-send settings: an icon chip in the section's
 * colour, a title with its one-line caption, then the controls — each
 * section its own card so the four never run together.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export const SettingsBlock: FC<{
  icon: LucideIcon;
  tint: string;
  title: string;
  caption: string;
  children: ReactNode;
}> = ({ icon: Icon, tint, title, caption, children }) => (
  <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 animate-in fade-in-0 slide-in-from-bottom-1">
    <div className="flex items-center gap-2.5">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold leading-tight">{title}</h3>
        <p className="text-xs text-muted-foreground">{caption}</p>
      </div>
    </div>
    {children}
  </section>
);

/** A small number field with its unit — the settings' one input shape. */
export const NumberField: FC<{
  value: number;
  onChange: (v: number) => void;
  label: string;
  width?: string;
  disabled?: boolean;
}> = ({ value, onChange, label, width = 'w-24', disabled }) => (
  <input
    type="number"
    aria-label={label}
    disabled={disabled}
    value={Number.isFinite(value) ? value : ''}
    onChange={(e) => onChange(Math.trunc(Number(e.target.value)))}
    className={`h-9 ${width} rounded-md border border-input bg-background px-2.5 text-sm font-semibold tabular-nums disabled:opacity-40`}
  />
);
